import {
	CaptureCardSessionPaymentMethodCommand,
	CaptureMobileCardSessionPaymentMethodCommand,
	CaptureSessionPaymentMethodCommand,
	CaptureWalletSessionPaymentMethodCommand,
	CardMethodType,
	WalletIdentifierType,
} from '@acorn-portal/shared/src/services/managedPaymentsApi';
import { BRAINTREE_PAYPAL_TYPE } from './resolve-payment-source';

/** The Braintree drop-in payment-method type for a physical card. */
export const BRAINTREE_CREDIT_CARD_TYPE = 'CreditCard';

/** The Braintree drop-in payment-method type for Google Pay (a mobile-wallet card). */
export const BRAINTREE_GOOGLE_PAY_TYPE = 'AndroidPayCard';

/** The Braintree drop-in payment-method type for Apple Pay (a mobile-wallet card). */
export const BRAINTREE_APPLE_PAY_TYPE = 'ApplePayCard';

export interface CapturePaymentMethodInput {
	sessionCorrelationId: string;
	saveCard: boolean;
	/** Braintree drop-in payment-method type, e.g. 'CreditCard', 'AndroidPayCard', 'ApplePayCard' or 'PayPalAccount'. */
	type: string;
	nonce: string;
	deviceData?: string;
	details?: {
		cardType?: string;
		lastFour?: string;
		expirationMonth?: string;
		expirationYear?: string;
		email?: string;
	};
}

/** Maps a Braintree mobile-wallet card type to its ICE card-method discriminator, or undefined if not a mobile-wallet card. */
const resolveMobileCardMethodType = (braintreeType: string): CardMethodType | undefined => {
	if (braintreeType === BRAINTREE_GOOGLE_PAY_TYPE) return CardMethodType.GOOGLE_PAY;
	if (braintreeType === BRAINTREE_APPLE_PAY_TYPE) return CardMethodType.APPLE_PAY;
	return undefined;
};

/**
 * Builds the polymorphic capture command for the CaptureSessionPaymentMethod endpoint from a tokenised
 * Braintree payment method. The "$type" discriminator is emitted first so it can be resolved during
 * deserialisation. Returns undefined for unsupported Braintree types.
 *
 * NOTE: the mobile-wallet card path (Apple Pay / Google Pay) is built and tested, but is not offered in the
 * live drop-in yet (the `noMobileWallets` flag hides those buttons) because ICE's mobile-card capture requires
 * a card expiry those network-tokenised instruments don't expose — on hold pending an ICE-side fix (see
 * Managed_Payments docs/ice-payment-issue.md).
 */
export const buildCapturePaymentMethodCommand = (
	input: CapturePaymentMethodInput,
): CaptureSessionPaymentMethodCommand | undefined => {
	const { sessionCorrelationId, saveCard, type, nonce, deviceData, details } = input;

	const base = {
		sessionCorrelationId,
		deviceData: deviceData ?? '',
		savePaymentMethod: saveCard,
	};

	if (type === BRAINTREE_PAYPAL_TYPE) {
		const command: CaptureWalletSessionPaymentMethodCommand = {
			$type: 'wallet',
			...base,
			walletToken: nonce,
			walletName: 'PayPal',
			identifierType: WalletIdentifierType.EMAIL,
			identifierValue: details?.email ?? '',
		};
		return command;
	}

	if (type === BRAINTREE_CREDIT_CARD_TYPE) {
		const command: CaptureCardSessionPaymentMethodCommand = {
			$type: 'card',
			...base,
			cardToken: nonce,
			cardType: details?.cardType ?? '',
			last4Digits: details?.lastFour ?? '',
			expiryMonth: details?.expirationMonth ? parseInt(details.expirationMonth) : 0,
			expiryYear: details?.expirationYear ? parseInt(details.expirationYear) : 0,
		};
		return command;
	}

	const cardMethodType = resolveMobileCardMethodType(type);
	if (cardMethodType) {
		// Apple Pay / Google Pay: no card-visible data — the scheme resolves the card from the token.
		const command: CaptureMobileCardSessionPaymentMethodCommand = {
			$type: 'mobileCard',
			...base,
			cardToken: nonce,
			cardMethodType,
		};
		return command;
	}

	return undefined;
};
